// File: src/MathTools/CircleTool.js
// ------------------------------------------------------
// A combined Circle + Trig Explorer:
//  - "Radius" slider affects the circle size.
//  - "Angle" slider (in degrees) draws a line at that angle
//    from the center, showing sin/cos as vertical/horizontal legs.
//
// You can further customize how you label or highlight tan(θ), etc.

import React, { useState } from "react";

export default function CircleTool() {
  const [radius, setRadius] = useState(3); // default radius
  const [angleDeg, setAngleDeg] = useState(30); // default angle in degrees
  const pi = 3.14;

  // Basic circle calculations
  const diameter = radius * 2;
  const circumference = (2 * pi * radius).toFixed(2);
  const area = (pi * radius * radius).toFixed(2);

  // For the SVG:
  const svgSize = 400;
  const cx = svgSize / 2;
  const cy = svgSize / 2;
  // We'll scale up so small radii are still visible.
  const scale = 25;
  const scaledRadius = radius * scale;

  // Convert angle from degrees to radians for actual drawing
  const angleRad = (Math.PI / 180) * angleDeg;

  // Coordinates of the point on the circle at "angleDeg"
  // Note: y is inverted in SVG, so we do cy - (...) if we want "up" to be positive.
  const px = cx + scaledRadius * Math.cos(angleRad);
  const py = cy - scaledRadius * Math.sin(angleRad);

  // We'll also draw a small right-triangle showing "adjacent" and "opposite" legs:
  //  adjacent = radius*cos(θ)
  //  opposite = radius*sin(θ)
  // In pixel terms, that's scaled by "scale".
  // So the horizontal leg goes from (cx,cy) to (pxH, cy),
  // and the vertical leg from (pxH,cy) to (pxH,py).
  // Where pxH is the x-projection onto the horizontal axis from center.
  const pxH = cx + scaledRadius * Math.cos(angleRad);

  // We'll label sin(θ) and cos(θ) if we want. 
  // Because the circle radius might not be 1, note that:
  //   Opposite side = radius * sin(θ)
  //   Adjacent side = radius * cos(θ)

  const opp = (radius * Math.sin(angleRad)).toFixed(2);
  const adj = (radius * Math.cos(angleRad)).toFixed(2);

  return (
    <div style={{ border: "1px solid #ccc", padding: 10, borderRadius: 6 }}>
      <h4>Circle &amp; Trig Explorer</h4>
      
      {/* Sliders */}
      <div style={{ marginBottom: 8 }}>
        <label style={{ marginRight: 10 }}>
          Radius: {radius}
        </label>
        <input
          type="range"
          min={1}
          max={10}
          value={radius}
          onChange={(e) => setRadius(parseFloat(e.target.value))}
          style={{ width: 120 }}
        />
      </div>

      <div style={{ marginBottom: 8 }}>
        <label style={{ marginRight: 10 }}>
          Angle (deg): {angleDeg}
        </label>
        <input
          type="range"
          min={0}
          max={360}
          value={angleDeg}
          onChange={(e) => setAngleDeg(parseFloat(e.target.value))}
          style={{ width: 120 }}
        />
      </div>

      <svg
        width={svgSize}
        height={svgSize}
        style={{ background: "#f9f9f9", display: "block", marginBottom: 10 }}
      >
        {/* Circle outline */}
        <circle
          cx={cx}
          cy={cy}
          r={scaledRadius}
          fill="#90caf9"
          stroke="#0d47a1"
          strokeWidth={2}
        />

        {/* Radius line at the chosen angle */}
        <line
          x1={cx}
          y1={cy}
          x2={px}
          y2={py}
          stroke="red"
          strokeWidth={3}
        />

        {/* A small "angle arc" to show the angle near the center */}
        <path
          d={describeArc(cx, cy, scaledRadius*0.2, -angleDeg, 0)}
          fill="none"
          stroke="red"
          strokeWidth={2}
        />
        {/* Possibly label the angle in degrees */}
        <text
          x={cx + 20 * Math.cos(angleRad / 2)} 
          y={cy - 20 * Math.sin(angleRad / 2)}
          fill="red"
          fontSize="14"
        >
          {angleDeg}°
        </text>

        {/* Horizontal leg of the triangle (from center horizontally to pxH) */}
        <line
          x1={cx}
          y1={cy}
          x2={pxH}
          y2={cy}
          stroke="green"
          strokeWidth={2}
          strokeDasharray="3,3"
        />
        {/* Vertical leg (from pxH,cy up to pxH,py) */}
        <line
          x1={pxH}
          y1={cy}
          x2={pxH}
          y2={py}
          stroke="green"
          strokeWidth={2}
          strokeDasharray="3,3"
        />

        {/* We can label "adjacent" and "opposite" if space allows */}
        <text
          x={(cx + pxH) / 2}
          y={cy - 5}
          fill="green"
          fontSize="14"
          textAnchor="middle"
        >
          Adjacent
        </text>
        <text
          x={pxH + 10}
          y={(cy + py) / 2}
          fill="green"
          fontSize="14"
        >
          Opp.
        </text>
      </svg>

      {/* Info about circle + trig */}
      <div style={{ marginBottom: 8 }}>
        <strong>Circle Properties:</strong>
        <br />
        <strong>Radius:</strong> {radius}, <strong>Diameter:</strong> {diameter},{" "}
        <strong>Circumference:</strong> {circumference}, <strong>Area:</strong>{" "}
        {area}
      </div>

      <div>
        <strong>Trig Components (with radius={radius}, angle={angleDeg}°):</strong>
        <p>
          Opposite side (≈ r·sinθ): {opp} <br />
          Adjacent side (≈ r·cosθ): {adj}
        </p>
      </div>

      <p style={{ fontSize: "0.95rem", color: "#777" }}>
        Using π ≈ 3.14. 
        <br/>
        <em>
          Notice how <code>sin(θ) = opposite / radius</code>, 
          <code>cos(θ) = adjacent / radius</code>. If radius=1 (unit circle),
          sin(θ) &amp; cos(θ) are directly the y and x coordinates of the circle point.
        </em>
      </p>
    </div>
  );
}

/**
 * A helper function that returns a path "d" for an arc 
 * center(cx,cy), radius=r, going from startAngle (deg) to endAngle (deg).
 * Negative angles for clockwise, etc. We'll keep it simple.
 */
function describeArc(cx, cy, r, startAngleDeg, endAngleDeg) {
  // Convert degrees to radians
  const startRad = (Math.PI / 180) * startAngleDeg;
  const endRad = (Math.PI / 180) * endAngleDeg;

  const largeArcFlag = Math.abs(endAngleDeg - startAngleDeg) <= 180 ? 0 : 1;

  const startX = cx + r * Math.cos(-startRad);
  const startY = cy + r * Math.sin(-startRad);
  const endX = cx + r * Math.cos(-endRad);
  const endY = cy + r * Math.sin(-endRad);

  return [
    `M ${startX},${startY}`,
    `A ${r},${r} 0 ${largeArcFlag} ${startAngleDeg < endAngleDeg ? 1 : 0} ${endX},${endY}`
  ].join(" ");
}
